import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { SvgWithFont } from '../utils/SvgWithFont';
import type { Visualization } from '../types';
import { MARGIN_HORIZONTAL, MIN_PADDING } from './constants';
import { ScalesOverallAverage } from './ScalesOverallAverage';
import { ScalesArea } from './ScalesArea';
import { ScalesAverageScore } from './ScalesAverageScore';
import { ScalesDistribution } from './ScalesDistribution';
import { ScalesSkips } from './ScalesSkips';
import type { ScalesData, ScalesOptions, ScalesScore } from './types';
import { getOverallAverage } from './helpers';

export interface ScalesVisualizationProps
  extends Omit<
    Visualization<ScalesData, ScalesOptions>,
    | 'valueAccessor'
    | 'keyAccessor'
    | 'skipValueAccessor'
    | 'width'
    | 'height'
    | 'colors'
  > {
  rangeMin: number;
  valueAccessor: (d: ScalesData) => ScalesScore;
  skipValueAccessor: (d: ScalesData) => number;
  isShowAverageEnabled?: boolean;
  shouldShowScoreTextValue?: boolean | undefined;
  overallAverageColor: string;
  onScaleValueClick: ((props: any) => void) | undefined;
  activeScale: {
    choiceId: ScalesOptions['id'] | null;
    value: number | null;
  };
  backgroundColor: string;

  svgWidth: number;
  svgHeight: number;
  xScale: any;
  yScale: any;
  boundsTopMargin: number;
  fontFamily: string;
  itemYScale: any;
  itemHeight: number;
  boundedSvgWidth: number;
  disableAnimations: boolean;
  fillColors: (id: string) => string;
  keyAccessor: (d: ScalesData | ScalesOptions) => string;
}

export const ScalesVisualization = ({
  svgWidth,
  svgHeight,
  fontFamily,
  isShowAverageEnabled,
  overallAverageColor,
  textColor,
  xScale,
  boundsTopMargin,
  staticOptions,
  keyAccessor,
  data,
  valueAccessor,
  skipValueAccessor,
  fillColors,
  yScale,
  itemYScale,
  itemHeight,
  boundedSvgWidth,
  backgroundColor,
  onScaleValueClick,
  activeScale,
  disableAnimations,
  rangeMin,
  shouldShowScoreTextValue = true,
}: ScalesVisualizationProps) => {
  const t = useTranslations('presenting');

  const [showDistribution, setShowDistribution] = useState<
    string | undefined
  >();
  const overallAverage = getOverallAverage(data, rangeMin);
  const hasData = data.length > 0;

  const styleNeededForOverallAverageToBeVisible = { overflow: 'visible' };

  return (
    <SvgWithFont
      width={svgWidth}
      height={svgHeight}
      style={styleNeededForOverallAverageToBeVisible}
      fontFamily={fontFamily}
      aria-hidden
    >
      <g
        data-testid="horizontal-bounds-container"
        style={{
          transform: `translateX(${MARGIN_HORIZONTAL}px)`,
        }}
      >
        {isShowAverageEnabled && hasData && (
          <ScalesOverallAverage
            color={overallAverageColor}
            overallAverage={overallAverage}
            textColor={textColor}
            xScale={xScale}
            height={svgHeight}
            disableAnimations={disableAnimations}
          />
        )}
        <g
          data-testid="vertical-bounds-container"
          style={{
            transform: `translateY(${boundsTopMargin}px)`,
          }}
        >
          {staticOptions.map((option) => {
            const id = keyAccessor(option);
            const value = data.find((d) => keyAccessor(d) === id);
            const scoresValue = value ? valueAccessor(value) : undefined;
            const hasScores =
              scoresValue && Object.keys(scoresValue).length > 0;
            const scores = hasScores ? scoresValue : undefined;
            const skips = value ? skipValueAccessor(value) : 0;
            const fill = fillColors(id);

            const isShowingDistribution = showDistribution === id;
            return (
              <g
                key={id}
                data-testid="option-container"
                onMouseEnter={() => setShowDistribution(id)}
                onMouseLeave={() => setShowDistribution(undefined)}
                style={{
                  transform: `translate(0,${yScale(id)}px)`,
                }}
              >
                <rect
                  data-testid="scale-hover-area-expander"
                  x={0}
                  y={0}
                  width={boundedSvgWidth}
                  height={itemHeight}
                  opacity={0}
                />
                {scores && (
                  <ScalesArea
                    fill={fill}
                    scores={scores}
                    xScale={xScale}
                    yScale={itemYScale}
                    showDistribution={isShowingDistribution}
                    paddingBottom={Math.max(itemHeight * 0.1, MIN_PADDING)}
                    disableAnimations={disableAnimations}
                  />
                )}
                <ScalesAverageScore
                  scores={scores ?? {}}
                  fill={fill}
                  xScale={xScale}
                  height={itemHeight}
                  shouldShowAverageCircle={isShowingDistribution || !hasData}
                  shouldShowScoreTextValue={Boolean(shouldShowScoreTextValue)}
                  totalWidth={boundedSvgWidth}
                  y={itemHeight}
                  placeholderBarColor={textColor}
                  disableAnimations={disableAnimations}
                />

                {scores && (
                  <>
                    <ScalesDistribution
                      isVisible={isShowingDistribution}
                      fill={fill}
                      outlineColor={backgroundColor}
                      scores={scores}
                      xScale={xScale}
                      height={itemHeight}
                      disableAnimations={disableAnimations}
                      onScaleValueClick={
                        onScaleValueClick &&
                        ((value: number) =>
                          onScaleValueClick({ choiceId: option.id, value }))
                      }
                      activeValue={
                        option.id === activeScale.choiceId
                          ? activeScale.value
                          : null
                      }
                    />
                    <ScalesSkips
                      x={boundedSvgWidth}
                      height={itemHeight}
                      isSkipsVisible={isShowingDistribution}
                      textColor={textColor}
                    >
                      {t.rich('scales.number_of_skips_message', {
                        skips,
                      })}
                    </ScalesSkips>
                  </>
                )}
              </g>
            );
          })}
        </g>
      </g>
    </SvgWithFont>
  );
};
